import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Button, TextField, Chip, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Close, CardGiftcard, QrCodeScanner, CurrencyRupee, Timer, ErrorOutline, Lock, EventBusy, PersonOff, Check } from '@mui/icons-material';
import { useLookupGiftVoucherMutation } from '../../../services/giftVoucherApi';
import { GV, inr, inr2, fmtDate, round2, VOUCHER_STATUS } from './gvUtils';

const fmtT = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

function Steps({ step }) {
  const items = ['Code', 'PIN', 'Amount'];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {items.map((l, i) => {
        const n = i + 1; const done = step > n; const on = step === n;
        return (
          <React.Fragment key={l}>
            {i > 0 && <Box sx={{ flex: 1, height: '1px', bgcolor: '#E2E8F0', minWidth: 12 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.66rem', fontWeight: 800, color: on ? '#4F46E5' : '#94A3B8' }}>
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', bgcolor: done ? '#16A34A' : on ? '#4F46E5' : '#F1F5F9', color: done || on ? '#FFF' : '#64748B' }}>
                {done ? '✓' : n}
              </Box>{l}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

const ERR_ICON = { pin_locked: Lock, unavailable: EventBusy, customer_required: PersonOff };

function LookupContent({ mode, orderId, amountDue, onApply, onClose, onAddCustomer, alreadyApplied, isMobile }) {
  const apply = mode === 'apply';
  const [lookup, { isLoading }] = useLookupGiftVoucherMutation();
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [needPin, setNeedPin] = useState(false);
  const [error, setError] = useState(null); // { code, message, attempts }
  const [result, setResult] = useState(null);
  const [amount, setAmount] = useState('');
  const [ttl, setTtl] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);
  const pinRef = useRef(null);

  const reset = () => { setCode(''); setPin(''); setNeedPin(false); setError(null); setResult(null); setAmount(''); setTtl(0); setTimeout(() => codeRef.current?.focus(), 0); };

  useEffect(() => {
    if (!result?.token || ttl <= 0) return undefined;
    const t = setInterval(() => setTtl((s) => {
      if (s <= 1) {
        clearInterval(t);
        setResult(null); setNeedPin(false); setPin('');
        setError({ code: 'expired', message: 'Voucher verification expired — scan the voucher again.' });
        return 0;
      }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [result?.token]); // eslint-disable-line

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const doLookup = async () => {
    const clean = code.trim();
    if (!clean) { setError({ code: 'empty', message: 'Enter or scan a voucher code' }); return; }
    if (needPin && !/^\d{4,8}$/.test(pin)) { setError({ code: 'wrong_pin', message: 'Enter the PIN from the email' }); return; }
    setError(null);
    try {
      const res = await lookup({ code: clean, ...(needPin ? { pin } : {}), ...(apply && orderId ? { order_id: orderId } : {}) }).unwrap();
      const d = res?.data;
      if (apply && alreadyApplied.includes(d.gift_voucher.id)) { setError({ code: 'dup', message: 'This voucher is already on the bill.' }); return; }
      setResult(d);
      if (apply) {
        const suggested = Number(d.suggested_amount ?? Math.min(Number(d.gift_voucher.balance), amountDue));
        setAmount(suggested.toFixed(2));
        setTtl(d.token_expires_in || 900);
      }
    } catch (err) {
      const status = err?.status;
      const details = err?.data?.details || {};
      if (status === 429) { setCooldown(60); setError({ code: 'rate', message: 'Too many voucher lookups — wait a minute and try again.' }); return; }
      if (details.code === 'pin_required') { setNeedPin(true); setError(null); setTimeout(() => pinRef.current?.focus(), 50); return; }
      if (details.code === 'wrong_pin') {
        setNeedPin(true); setPin('');
        const left = details.attempts_remaining;
        setError({ code: 'wrong_pin', message: `Wrong PIN.${left != null ? ` ${left} attempt${left !== 1 ? 's' : ''} left before the voucher locks.` : ''}` });
        setTimeout(() => pinRef.current?.focus(), 50);
        return;
      }
      if (details.code === 'pin_locked') { setError({ code: 'pin_locked', message: 'Locked after 5 wrong PINs — ask an admin to unlock it.' }); return; }
      if (details.code === 'customer_required') { setError({ code: 'customer_required', message: 'Attach a customer to the bill before paying with a gift voucher.' }); return; }
      if (details.code === 'not_found' || status === 404) { setError({ code: 'not_found', message: 'We couldn’t find that voucher. Check the code and try again.' }); return; }
      setError({ code: details.code || 'error', message: err?.data?.error || 'Could not check the voucher' });
    }
  };

  const suggested = Number(result?.suggested_amount ?? 0);
  const amt = parseFloat(amount) || 0;
  const amtErr = !apply || !result ? null
    : !(amt > 0) ? 'Enter an amount above zero'
      : amt > suggested + 0.001 ? `You can use at most ${inr2(suggested)} — lower it, never raise it`
        : /\.\d{3,}/.test(amount) ? 'At most 2 decimal places' : null;

  const handleApply = () => {
    if (amtErr) return;
    onApply({
      voucherId: result.gift_voucher.id, masked: result.gift_voucher.masked_code, typeName: result.gift_voucher.gift_voucher_type_name,
      token: result.token, amount: round2(amt), balanceAfter: round2(Number(result.gift_voucher.balance) - amt),
    });
  };

  const step = result ? 3 : needPin ? 2 : 1;
  const v = result?.gift_voucher;
  const vs = v ? VOUCHER_STATUS[v.status] || VOUCHER_STATUS.active : null;
  const ErrIcon = ERR_ICON[error?.code] || ErrorOutline;

  return (
    <>
      <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: GV.bg, color: GV.main, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><CardGiftcard sx={{ fontSize: 20 }} /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1E293B' }}>{apply ? 'Apply gift voucher' : 'Check voucher balance'}</Typography>
          <Typography noWrap sx={{ fontSize: '0.7rem', color: '#94A3B8' }}>{apply ? `Amount due ${inr2(amountDue)}` : 'Code and PIN are never stored on this terminal'}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }} aria-label="Close"><Close sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {apply && <Steps step={step} />}
        <Box>
          <Typography component="label" htmlFor="gv-code" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B' }}>Voucher code</Typography>
          <TextField id="gv-code" inputRef={codeRef} autoFocus fullWidth value={code} disabled={!!result} autoComplete="off"
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setNeedPin(false); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doLookup(); } }}
            placeholder="Scan the QR or type the 16-character code"
            InputProps={{ startAdornment: <QrCodeScanner sx={{ color: GV.main, fontSize: 22, mr: 1 }} /> }}
            error={['not_found', 'empty', 'dup'].includes(error?.code)}
            sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { height: 48, borderRadius: '10px', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.06em' }, '& input::placeholder': { letterSpacing: 'normal', fontSize: '0.85rem', fontWeight: 500 } }} />
        </Box>
        {needPin && !result && (
          <Box>
            <Typography component="label" htmlFor="gv-pin" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B' }}>PIN</Typography>
            <TextField id="gv-pin" inputRef={pinRef} fullWidth type="password" value={pin} placeholder="PIN from the email" autoComplete="off"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doLookup(); } }}
              error={error?.code === 'wrong_pin'} inputProps={{ inputMode: 'numeric' }}
              sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { height: 44, borderRadius: '10px', fontSize: '1.05rem', letterSpacing: '0.3em' } }} />
          </Box>
        )}
        {error && error.code !== 'empty' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 1.4, py: 1, borderRadius: '8px', bgcolor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '0.72rem' }}>
            <ErrIcon sx={{ fontSize: 17 }} /><Box sx={{ flex: 1 }}>{error.message}</Box>
            {error.code === 'customer_required' && onAddCustomer && (
              <Button size="small" onClick={onAddCustomer} sx={{ flexShrink: 0, height: 28, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.68rem', color: '#64748B', border: '1px solid #E2E8F0', bgcolor: '#FFF' }}>Add customer</Button>
            )}
          </Box>
        )}
        {v && (
          <Box sx={{ borderRadius: '12px', p: 1.75, bgcolor: GV.bg, border: `1px solid ${GV.border}`, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: GV.text }}>{v.gift_voucher_type_name}</Typography>
              <Chip size="small" label={vs.label} sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: vs.bg, color: vs.color, borderRadius: '6px' }} />
            </Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem' }}>{v.masked_code}</Typography>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#0F172A', lineHeight: 1.1 }}>{inr2(v.balance)}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#64748B' }}>of {inr(v.face_value)} · {v.expires_at ? `valid till ${fmtDate(v.expires_at)}` : 'no expiry'}</Typography>
          </Box>
        )}
        {apply && result && (
          <Box>
            <Typography component="label" htmlFor="gv-amt" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B' }}>Use on this bill</Typography>
            <TextField id="gv-amt" fullWidth value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} error={!!amtErr}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApply(); } }}
              InputProps={{ startAdornment: <CurrencyRupee sx={{ fontSize: 18, color: '#64748B', mr: 0.5 }} /> }} inputProps={{ inputMode: 'decimal' }}
              sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { height: 44, borderRadius: '10px', fontSize: '1.05rem', fontWeight: 800 } }} />
            <Typography sx={{ fontSize: '0.66rem', color: amtErr ? '#DC2626' : '#94A3B8', mt: 0.5 }}>
              {amtErr || `Up to ${inr2(suggested)} (the smaller of balance and amount due). ${inr2(round2(Number(v.balance) - amt))} stays on the voucher.`}
            </Typography>
            <Typography sx={{ fontSize: '0.66rem', color: '#64748B', mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Timer sx={{ fontSize: 14 }} /> Verification valid for <b>{fmtT(ttl)}</b> on this bill and terminal.
            </Typography>
          </Box>
        )}
        {!apply && result && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8' }}>Read-only check. Nothing is reserved on the voucher.</Typography>}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px solid #F1F5F9', mt: 'auto', '& .MuiButton-root': { minHeight: 38, borderRadius: '8px', textTransform: 'none', fontWeight: 700, ...(isMobile ? { flex: 1 } : {}) } }}>
        {result && !apply ? (
          <>
            <Button onClick={reset} sx={{ border: '1px solid #E2E8F0', color: '#64748B' }}>Check another</Button>
            <Button variant="contained" disableElevation onClick={onClose} sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' } }}>Done</Button>
          </>
        ) : result && apply ? (
          <>
            <Button onClick={onClose} sx={{ border: '1px solid #E2E8F0', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" disableElevation onClick={handleApply} disabled={!!amtErr} startIcon={<Check />} sx={{ bgcolor: GV.main, '&:hover': { bgcolor: GV.dark } }}>Apply {inr2(amt)}</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} sx={{ border: '1px solid #E2E8F0', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" disableElevation onClick={doLookup} disabled={isLoading || cooldown > 0 || error?.code === 'pin_locked'} sx={{ bgcolor: GV.main, '&:hover': { bgcolor: GV.dark } }}>
              {isLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : cooldown > 0 ? `Wait ${cooldown}s` : needPin ? 'Verify PIN' : 'Look up'}
            </Button>
          </>
        )}
      </Box>
    </>
  );
}

/**
 * mode="apply":   verify a voucher for the current bill and return
 *                 { masked, balanceAfter, typeName, token, amount, voucherId } via onApply.
 * mode="balance": read-only balance check (no order, no token).
 */
export default function GiftVoucherLookupDialog({ open, mode = 'balance', orderId, amountDue = 0, onApply, onClose, onAddCustomer, alreadyApplied = [] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', m: { xs: 0, sm: 3 } } }}>
      {open && (
        <LookupContent mode={mode} orderId={orderId} amountDue={amountDue} onApply={onApply} onClose={onClose}
          onAddCustomer={onAddCustomer} alreadyApplied={alreadyApplied} isMobile={isMobile} />
      )}
    </Dialog>
  );
}
